import { makeAutoObservable, runInAction } from 'mobx';
import { WizardStep } from 'models';
import { IntegrationStatus } from '../views/Integrations/components/IntegrationCard/DataStatusIcon/DataStatusIcon';

export enum PartnerId {
  JohnDeere = 'john_deere',
  CNH = 'cnh',
  Proagrica = 'proagrica',
  TrapView = 'trap_view',
  Arable = 'arable',
  Sencrop = 'sencrop',
  GroGuru = 'groguru',
  Veris = 'veris',
  Pessl = 'pessl',
  Valenco = 'valenco',
  AgFiniti = 'agfiniti',
  Farmobile = 'farmobile',
  Perfect_flight = 'perfect_flight',
  Trimble = 'trimble_agriculture',
  Trimble2 = 'trimble',
}

export enum IntegrationKeys {
  TrapviewSaa = 'trap_view.saa',
  AgfinitiAgfiniti = 'agfiniti.agfiniti',
  CnhiCnhi = 'cnhi.cnhi',
  PerfectFlightPerfectFlight = 'perfect_flight.perfect_flight',
  TrapviewPlanet = 'trap_view.planet',
  TrapviewSiagri = 'trap_view.siagri',
  TrapviewNectar = 'trap_view.nectar',
  TrapviewNemadigital = 'trap_view.nemadigital',
  TrapviewTrapView = 'trap_view.trap_view',
  JohnDeereOperationsCenter = 'john_deere.operation_center',
  JohnDeereJohnDeereOperationsCenter = 'john_deere.john_deere_operations_center',
  Proagrica = 'proagrica.proagrica',
  Farmobile = 'farmobile.farmobile',
  Arable = 'arable.arable',
  Sencrop = 'sencrop.sencrop',
  GroGuru = 'groguru.groguru',
  Pessl = 'pessl.pessl',
  TrimbleTrimble = 'trimble_agriculture.trimble',
}

export const v3Integrations = {
  [PartnerId.JohnDeere.toString()]: IntegrationKeys.JohnDeereOperationsCenter,
  [PartnerId.Trimble.toString()]: IntegrationKeys.TrimbleTrimble,
  [PartnerId.Trimble2.toString()]: IntegrationKeys.TrimbleTrimble,
};

export enum IntegrationConnectionType {
  machinery = 'machinery',
  observation = 'observations',
}

export interface SupportedApplication {
  id: string;
  name: string;
  icon_uri: string;
  image_uri: string;
}

export type languagesValues = {
  [language: string]: string;
};

export interface ConnectIntegrationResponse {
  name: string;
  provider: string;
  source: string;
  description: languagesValues;
  description_extended?: languagesValues;
  countries: string[];
  classes: string[];
  supported_applications: SupportedApplication[];
  required_applications: any[];
  icon: string;
  image: string | null;
  setup_url?: string | null;
  entitlement_key?: string | null;
  tags: string[];
  partner_enum?: string;
  id: string;
}

interface App {
  plan: Plan;
  id: string;
  contract: Contract;
}

interface Plan {
  id: string;
  name: string;
  entitlements: Entitlement[];
}

interface Contract {
  id: string;
  start: string;
  end: string;
  notes: string;
  waiting_for_payment: boolean;
  deleted: boolean;
}

interface Entitlement {
  id: string;
  key: string;
  name: string;
}

export interface LicenseStatusResponse {
  apps: App[];
}

export const OAUTH_PARTNERS = [
  PartnerId.JohnDeere.toString(),
  PartnerId.CNH.toString(),
  PartnerId.AgFiniti.toString(),
  PartnerId.Farmobile.toString(),
];

export const BASIC_AUTH_PARTNERS = [
  PartnerId.Arable.toString(),
  PartnerId.Sencrop.toString(),
  PartnerId.TrapView.toString(),
  PartnerId.GroGuru.toString(),
  PartnerId.Veris.toString(),
  PartnerId.Valenco.toString(),
  PartnerId.Perfect_flight.toString(),
];

export const ALL_AUTH_PARTNERS = [
  PartnerId.Pessl.toString(),
  ...OAUTH_PARTNERS,
  ...BASIC_AUTH_PARTNERS,
];

export const TRANSLATIONS_AUTH_PARTNERS = [
  PartnerId.AgFiniti.toString(),
  PartnerId.Farmobile.toString(),
];

export const dateFormat = 'yyyy-LL-dd';

export const USER_ORG_ID = 'USER_ORG_ID';

export const AUTH_ACCOUNT_STATUS = {
  ACTIVE: 'ACTIVE',
  INACTIVE: 'INACTIVE',
};

export class Integrations {
  _integrations: Integration[] = [];

  constructor(_ints: any[] = []) {
    makeAutoObservable(this);
    _ints.forEach((int: any) => {
      this.integrations.push(new Integration(int));
    });
  }

  findIntegrationByPartner(partner_id: string): Integration | undefined {
    if (partner_id === PartnerId.CNH) {
      return this.integrations.find(
        (integration: Integration) => integration.integrationKey === IntegrationKeys.CnhiCnhi,
      );
    }
    if (partner_id === PartnerId.Trimble2 || partner_id === PartnerId.Trimble) {
      return this.integrations.find(
        (integration: Integration) => integration.integrationKey === IntegrationKeys.TrimbleTrimble,
      );
    }
    return this.integrations.find(
      (integration: Integration) => integration.partner_id === partner_id,
    );
  }

  isEmpty() {
    return this.integrations.length === 0;
  }

  find_by_integration_key(integration_key: string) {
    const integration = this.integrations.find(
      (integration: Integration) => integration.integrationKey === integration_key,
    );
    return integration;
  }
  get_all_integrations() {
    return this.integrations;
  }

  set integrations(ints: Integration[]) {
    runInAction(() => {
      this._integrations = ints;
    });
  }
  get integrations() {
    return this._integrations;
  }
}

class Integration {
  id: string = '';
  _user_id: string = '';
  integration_name: string;
  image: string;
  logo: string;
  logo_big: string;
  source: string;
  description: Record<string, string>;
  description_extended: Record<string, string>;
  connection_type: IntegrationConnectionType;
  is_paid: boolean;
  is_beta: boolean;
  partner_id: PartnerId;
  stage: null;
  auth_config: any;
  setup_url?: null | string;
  wizardSteps: WizardStep[] = [];
  _settings?: any = {};
  showAutofft: boolean;
  org_id!: string;
  status?: IntegrationStatus;
  classes?: string[];
  supportedApplications?: SupportedApplication[];
  requiredApplications?: string[];
  integrationKey: string;

  constructor(_integration: any) {
    makeAutoObservable(this);
    this.id = _integration.id;
    this.integration_name = _integration.name;
    this.image = _integration.image;
    this.logo = _integration.icon;
    this.logo_big = _integration.icon;
    this.source = _integration.source;
    this.description = _integration.description;
    this.description_extended = _integration.description_extended;
    this.connection_type = _integration.connection_type;
    this.is_paid = this.includeTag(_integration.tags, 'paid');
    this.is_beta = this.includeTag(_integration.tags, 'beta');
    this.partner_id = _integration.partner_enum;
    this.setup_url = _integration.setup_url;
    this.stage = _integration.stage;
    this.wizardSteps = _integration.wizardSteps;
    this.showAutofft = _integration.showAutofft;
    this.status = _integration.status;
    this.classes = _integration.classes;
    this.supportedApplications = _integration.supported_applications;
    this.requiredApplications = _integration.required_applications;
    this.integrationKey = _integration.integrationKey;
  }

  includeTag(tags: string[], tag: string) {
    return tags?.includes(tag);
  }

  get name() {
    return this.integration_name;
  }

  get user_id() {
    return this._user_id;
  }

  set user_id(id: string) {
    this._user_id = id;
  }

  get is_configured() {
    return !!this.auth_config;
  }

  set is_configured(auth_config: any) {
    runInAction(() => {
      this.auth_config = auth_config;
    });
  }

  get settings() {
    return this._settings;
  }

  set settings(_settings: any) {
    runInAction(() => {
      this._settings = _settings;
    });
  }

  updateIntegrationStartDate(startDate: string) {
    if (this._settings) {
      runInAction(() => {
        this._settings.start_time = startDate;
      });
    }
  }
}

export default Integration;
